import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldAlert, Plus } from "lucide-react";

const GRAV = {
  leve: { l: "Leve", c: "bg-[#F4F4F5] text-aj-text" },
  moderada: { l: "Moderada", c: "bg-aj-amber-soft text-aj-orange-hover" },
  grave: { l: "Grave", c: "bg-[#FED7AA] text-[#9A3412]" },
  critica: { l: "Crítica", c: "bg-[#E60000] text-white" },
};
const TIPOS = [
  ["descumprimento_agenda", "Descumprimento de agenda"],
  ["qualidade", "Qualidade do serviço"],
  ["assistencia_excessiva", "Assistências em excesso"],
  ["conduta", "Conduta / postura"],
  ["comunicacao", "Falha de comunicação"],
  ["outro", "Outro"],
];

export default function OcorrenciasMontadora() {
  const { user } = useAuth();
  const podeRegistrar = ["empresa", "admin"].includes(user?.role);
  const [docs, setDocs] = useState(null);
  const [montadoras, setMontadoras] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ montadora_id: "", gravidade: "", tipo: "", descricao: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/ocorrencias-montadora").then(r => setDocs(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
    if (user?.role === "admin") {
      api.get("/montadoras").then(r => setMontadoras(r.data || [])).catch(() => {});
    } else if (user?.role === "empresa") {
      api.get("/empresa/pool-montadoras").then(r =>
        setMontadoras((r.data.pool || []).map(p => ({ id: p.montadora_id, nome: p.nome })))).catch(() => {});
    }
  }, [load, user]);

  const salvar = async () => {
    if (!form.montadora_id || !form.gravidade || !form.tipo) { toast.error("Preencha montadora, gravidade e tipo"); return; }
    if ((form.descricao || "").trim().length < 10) { toast.error("Descreva a ocorrência (mín. 10 caracteres)"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/montadoras/${form.montadora_id}/ocorrencias`, {
        gravidade: form.gravidade, tipo: form.tipo, descricao: form.descricao.trim(),
      });
      const efeito = r.data.efeito;
      toast.success(efeito === "suspensa_automaticamente"
        ? "Ocorrência CRÍTICA registrada — montadora suspensa automaticamente."
        : efeito === "em_observacao_por_recorrencia"
        ? "Ocorrência registrada — montadora entrou em observação por recorrência."
        : "Ocorrência registrada.");
      setFormOpen(false); setForm({ montadora_id: "", gravidade: "", tipo: "", descricao: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="ocorrencias-montadora-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Rede</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Ocorrências de Montadora</h1>
          <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
            {user?.role === "montadora"
              ? "Ocorrências registradas sobre a sua montadora. Crítica suspende automaticamente; recorrência coloca em observação."
              : "Registre ocorrências graduadas (leve → crítica). Crítica suspende a montadora automaticamente; 3+ moderadas em 30 dias colocam em observação."}
          </p>
        </div>
        {podeRegistrar && (
          <Button onClick={() => setFormOpen(!formOpen)} className="btn-aj-primary" data-testid="oc-mt-nova">
            <Plus size={15} /> Registrar ocorrência
          </Button>
        )}
      </div>

      {formOpen && podeRegistrar && (
        <div className="aj-card p-5 space-y-3" data-testid="oc-mt-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <Select value={form.montadora_id} onValueChange={v => setForm(f => ({ ...f, montadora_id: v }))}>
              <SelectTrigger data-testid="oc-mt-montadora"><SelectValue placeholder="Montadora" /></SelectTrigger>
              <SelectContent>
                {montadoras.map(m => <SelectItem key={m.id} value={m.id}>{m.nome || m.razao_social}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.gravidade} onValueChange={v => setForm(f => ({ ...f, gravidade: v }))}>
              <SelectTrigger data-testid="oc-mt-gravidade"><SelectValue placeholder="Gravidade" /></SelectTrigger>
              <SelectContent>
                {Object.entries(GRAV).map(([k, g]) => <SelectItem key={k} value={k}>{g.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
              <SelectTrigger data-testid="oc-mt-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                {TIPOS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.gravidade === "critica" && (
            <div className="text-xs font-semibold text-[#B91C1C] bg-[#FEE2E2] rounded p-2">
              ⚠️ Ocorrência CRÍTICA suspende a montadora automaticamente (não recebe novas OS até revisão do ADM).
            </div>
          )}
          <Textarea rows={3} placeholder="Descreva o que aconteceu (mín. 10 caracteres)"
            value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            data-testid="oc-mt-descricao" />
          <Button onClick={salvar} disabled={saving} className="btn-aj-primary" data-testid="oc-mt-salvar">
            {saving ? "Registrando..." : "Registrar"}
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {docs === null && (
          <div className="aj-card p-10 text-center text-aj-text-muted">Carregando…</div>
        )}
        {docs?.length === 0 && (
          <div className="aj-card p-10 text-center text-aj-text-muted" data-testid="oc-mt-vazio">
            <ShieldAlert className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nenhuma ocorrência registrada.
          </div>
        )}
        {docs?.map(o => {
          const g = GRAV[o.gravidade] || GRAV.leve;
          const tipoLabel = (TIPOS.find(([k]) => k === o.tipo) || [null, o.tipo])[1];
          return (
            <div key={o.id} className={`aj-card p-5 ${o.gravidade === "critica" ? "border-l-4 border-l-[#E60000]" : ""}`}
              data-testid={`oc-mt-card-${o.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${g.c}`}>{g.l}</span>
                <span className="font-display font-bold text-aj-blue-deep">{o.montadora_nome}</span>
                <span className="text-xs text-aj-text-muted">· {tipoLabel}</span>
              </div>
              <p className="text-sm text-aj-text mt-2">{o.descricao}</p>
              <div className="text-xs text-aj-text-muted mt-2">
                {new Date(o.criado_em).toLocaleString("pt-BR")}
                {o.autor_nome ? ` · por ${o.autor_nome}` : ""}
                {o.servico_id ? ` · OS ${o.servico_id}` : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
